
















































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import { mixins } from "vue-class-component";

import buildUrl from "build-url";
import VuePlotly from "@statnett/vue-plotly";

import { endpoints } from "@/endpoints";
import { formatForMetric } from "./formatters";
import { labelForMetric } from "./catalogHelpers";
import { applyFilterParams, RcaFilters } from "./filterQueryParams";
import RcaEndpointData from "./data-mixin";
import PlotlyResizeMixin from "./plotly-resize-mixin";

interface MetricOption {
    /** Name of the column whose daily values are plotted. */
    key: string;
    /** The metric's id in the catalog, which is where its name comes from. */
    metricId: string;
}

/** The seven headline metrics, in the same order as the KPI cards above the chart
 * (see KPI_SPECS in KpiCards.vue). These sort to the top of the metric menu so the
 * chart offers the KPIs first.
 *
 * The two lists can't share a definition — the cards read the `overview_*_sum` metrics
 * (selected-range rollups) while the chart reads the `daily_*` ones — so if the cards
 * change, this list has to be updated to match by hand. */
const KPI_METRIC_OPTIONS: MetricOption[] = [
    { key: "closure_event_count", metricId: "daily_closure_event_count" },
    { key: "impacted_trip_count", metricId: "daily_impacted_trip_count" },
    {
        key: "endpoint_impacted_enterprise_count",
        metricId: "daily_endpoint_impacted_enterprise_count",
    },
    {
        key: "endpoint_rerouted_enterprise_count",
        metricId: "daily_endpoint_rerouted_enterprise_count",
    },
    {
        key: "endpoint_blocked_enterprise_count",
        metricId: "daily_endpoint_blocked_enterprise_count",
    },
    { key: "road_delta_time_hours", metricId: "daily_road_delta_time_hours" },
    { key: "road_delta_length_km", metricId: "daily_road_delta_length_km" },
];

/** Everything else the daily summary can plot, below the divider. */
const OTHER_METRIC_OPTIONS: MetricOption[] = [
    { key: "closed_link_count", metricId: "daily_closed_link_count" },
    { key: "rerouted_trip_count", metricId: "daily_rerouted_trip_count" },
    { key: "blocked_trip_count", metricId: "daily_blocked_trip_count" },
    { key: "impacted_tonnes", metricId: "daily_impacted_tonnes" },
    { key: "rerouted_tonnes", metricId: "daily_rerouted_tonnes" },
    { key: "blocked_tonnes", metricId: "daily_blocked_tonnes" },
];

/** Flat list — used to resolve the selected key back to its option. */
const METRIC_OPTIONS: MetricOption[] = [
    ...KPI_METRIC_OPTIONS,
    ...OTHER_METRIC_OPTIONS,
];

/** A day counts as "complete" when its completeness signal says so (the filtered daily-summary
 * branch only returns the raw code; the unfiltered branch returns the derived boolean). */
const isCompleteDay = (r: any): boolean => {
    if (typeof r.is_complete === "boolean") return r.is_complete;
    if (typeof r.overall_completeness_code === "string") {
        return r.overall_completeness_code === "complete";
    }
    return true; // neither field present — treat as complete rather than mass-flagging
};

@Component({ components: { VuePlotly } })
export default class TrendChart extends mixins(
    RcaEndpointData,
    PlotlyResizeMixin,
) {
    @Prop({ required: true }) startDate!: string;
    @Prop({ required: true }) endDate!: string;
    /** The metric catalog, which supplies the names in the metric menu. */
    @Prop({ default: null }) catalog!: any;
    /** Global sidebar filters. */
    @Prop({ default: () => ({}) }) filters!: RcaFilters;

    metric: string = "closure_event_count";

    /** Catalog label for one option. Empty until the catalog payload arrives - the metric menu itself is gated on `catalog` in the template. */
    private optionLabel(option: MetricOption): string {
        return labelForMetric(this.catalog, option.metricId);
    }

    /** What the v-select renders: KPI metrics, a divider, then the rest.
     * Computed (not static) so labels update once the catalog resolves. */
    get metricOptions(): any[] {
        const withLabels = (options: MetricOption[]) =>
            options.map((o) => ({ ...o, label: this.optionLabel(o) }));
        return [
            ...withLabels(KPI_METRIC_OPTIONS),
            { divider: true },
            ...withLabels(OTHER_METRIC_OPTIONS),
        ];
    }

    /** Catalog label for the currently selected metric — used as the Plotly trace name. */
    get selectedMetricLabel(): string {
        const option = METRIC_OPTIONS.find((o) => o.key === this.metric);
        return option ? this.optionLabel(option) : this.metric;
    }

    /** Per-metric typical-range bounds (P10/P50/P90 over all complete history), keyed by metric. */
    bounds: Record<
        string,
        { p10: number; p50: number; p90: number; n: number }
    > = {};

    mounted() {
        this.fetch();
        this.fetchBounds();
    }

    resizeCharts() {
        this.refitPlotly(["trendChart"]);
    }

    /** One key over every fetch input so a param change triggers exactly one fetch. */
    private get fetchParamsKey(): string {
        return `${this.startDate}|${this.endDate}|${JSON.stringify(
            this.filters,
        )}`;
    }

    @Watch("fetchParamsKey")
    onRangeChanged() {
        this.fetch();
    }

    fetch() {
        if (!this.startDate || !this.endDate) return;
        this.loadData(
            endpoints.roadClosureAnalyticsDailySummaryUrl,
            applyFilterParams(
                { start_date: this.startDate, end_date: this.endDate },
                this.filters,
            ),
        );
    }

    /** The bounds fetch depends only on the global filters (the history extent is resolved
     * server-side), so it isn't re-pulled when the user changes the selected window or metric. */
    private get boundsKey(): string {
        return JSON.stringify(this.filters);
    }

    @Watch("boundsKey")
    onBoundsKeyChanged() {
        this.fetchBounds();
    }

    /** Fetch the per-metric typical-range bounds (computed server-side over all complete history)
     * once per filter set; switching metric/window just reads precomputed data. */
    fetchBounds() {
        const params = applyFilterParams({}, this.filters);
        const queryParams: Record<string, any> = {};
        for (const k of Object.keys(params)) {
            const v = (params as any)[k];
            if (v === undefined || v === null || v === "") continue;
            queryParams[k] = v;
        }
        Vue.axios({
            url: buildUrl(endpoints.roadClosureAnalyticsDailyMetricBoundsUrl, {
                queryParams,
                disableCSV: true,
            }),
        })
            .then((r: any) => {
                const rows = Array.isArray(r.data) ? r.data : [];
                const map: Record<string, any> = {};
                for (const row of rows) map[row.metric] = row;
                this.bounds = map;
            })
            .catch(() => {
                this.bounds = {};
            });
    }

    /** Typical-range bounds for the selected metric, or null when the backend omitted it
     * (<8 days of history). */
    get bandStats(): {
        p10: number;
        p50: number;
        p90: number;
        n: number;
    } | null {
        return this.bounds[this.metric] ?? null;
    }

    get sortedData() {
        const rows = Array.isArray(this.data) ? [...this.data] : [];
        return rows.sort((a: any, b: any) =>
            String(a.scenario_date_local).localeCompare(
                String(b.scenario_date_local),
            ),
        );
    }

    get traces() {
        const rows = this.sortedData;
        const x = rows.map((r: any) =>
            String(r.scenario_date_local).slice(0, 10),
        );
        const y = rows.map((r: any) => Number(r[this.metric]) || 0);
        // Marker colour reflects completeness on each day (blue = complete, amber = flagged).
        // The unfiltered branch of get_daily_summary returns the derived `is_complete` boolean
        // (from the analysis.daily_summary view); the filtered branch only returns the raw
        // `overall_completeness_code` string. Fall back so a day is treated as complete when
        // either signal says so.
        const complete = rows.map(isCompleteDay);
        const markerColors = complete.map((c) => (c ? "#013e75" : "#f57c00"));
        const label = this.selectedMetricLabel;

        // Plotly can't dash individual segments of a single line, so split the line into two
        // traces: segments between two complete days stay solid, while any segment that touches
        // a non-complete (amber) day is drawn dashed to flag the data gap. Each trace lists its
        // segments as point-pairs separated by `null` so only those pairs are connected.
        const solidX: any[] = [];
        const solidY: any[] = [];
        const dashX: any[] = [];
        const dashY: any[] = [];
        for (let i = 0; i < rows.length - 1; i++) {
            const incomplete = !complete[i] || !complete[i + 1];
            const sx = incomplete ? dashX : solidX;
            const sy = incomplete ? dashY : solidY;
            sx.push(x[i], x[i + 1], null);
            sy.push(y[i], y[i + 1], null);
        }

        const lineBase = {
            type: "scatter",
            mode: "lines",
            hoverinfo: "skip",
            showlegend: false,
        };
        return [
            {
                ...lineBase,
                x: solidX,
                y: solidY,
                line: { color: "#013e75", width: 2 },
            },
            {
                ...lineBase,
                x: dashX,
                y: dashY,
                line: { color: "#013e75", width: 2, dash: "dash" },
            },
            {
                type: "scatter",
                mode: "markers",
                x,
                y,
                name: label,
                marker: { color: markerColors, size: 7 },
                // Manually build the text so hovering shows the value exactly as the KPI card above does.
                text: y.map((v) => formatForMetric(this.metric)(v)),
                hovertemplate: `%{x}<br>${label}: %{text}<extra></extra>`,
            },
        ];
    }

    get layout() {
        // Responsive date axis with automatic tick spacing and date formatting.
        // Enable the range slider only when enough data points are available.
        const sliderEnabled = this.sortedData.length > 60;
        const band = this.bandStats;
        const shapes: any[] = [];
        let yaxisRange: any = {};
        if (band) {
            // Typical-range band (P10–P90) + median, spanning the full plot width.
            shapes.push({
                type: "rect",
                xref: "paper",
                x0: 0,
                x1: 1,
                yref: "y",
                y0: band.p10,
                y1: band.p90,
                fillcolor: "rgba(1,62,117,0.07)",
                line: { width: 0 },
                layer: "below",
            });
            shapes.push({
                type: "line",
                xref: "paper",
                x0: 0,
                x1: 1,
                yref: "y",
                y0: band.p50,
                y1: band.p50,
                line: { color: "rgba(1,62,117,0.4)", width: 1, dash: "dot" },
                layer: "below",
            });
            // Keep the band visible even when the selected window sits entirely above/below it.
            const yvals = this.sortedData.map(
                (r: any) => Number(r[this.metric]) || 0,
            );
            yaxisRange = {
                range: [0, (Math.max(...yvals, band.p90, 0) || 1) * 1.08],
            };
        }
        return {
            shapes,
            dragmode: false,
            // Show only the nearest line's value on hover, not every overlaid metric at once.
            hovermode: "closest",
            height: sliderEnabled ? 580 : 520,
            margin: { l: 50, r: 16, t: 8, b: sliderEnabled ? 36 : 70 },
            xaxis: {
                type: "date",
                automargin: true,
                tickangle: sliderEnabled ? 0 : -45,
                nticks: sliderEnabled ? 10 : 12,
                rangeslider: sliderEnabled
                    ? {
                          visible: true,
                          thickness: 0.06,
                          bgcolor: "rgba(1,62,117,0.06)",
                      }
                    : undefined,
                tickformatstops: [
                    { dtickrange: [null, 86400000 * 7], value: "%b %d" },
                    {
                        dtickrange: [86400000 * 7, "M3"],
                        value: "%b %d",
                    },
                    { dtickrange: ["M3", "M24"], value: "%b %Y" },
                    { dtickrange: ["M24", null], value: "%Y" },
                ],
            },
            yaxis: {
                automargin: true,
                gridcolor: "rgba(0,0,0,0.06)",
                ...yaxisRange,
            },
            plot_bgcolor: "rgba(0,0,0,0)",
            paper_bgcolor: "rgba(0,0,0,0)",
            showlegend: false,
        };
    }
}
